import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { payslipService, userService } from '../services/api';
import {
  FileText, CheckCircle, XCircle, Clock, Download, Eye, Plus,
  Trash2, Check, CreditCard, UploadCloud, Paperclip, X, AlertCircle, ExternalLink,
  FileCheck, CheckCircle2
} from 'lucide-react';
import pdfService from '../services/pdfService';
import toast from 'react-hot-toast';
import PayslipDetail from './PayslipDetail';
import ConfirmModal from './ConfirmModal';

const PayslipsManagement = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isManager = ['GERENCIA', 'ADMIN', 'CEO'].includes(user?.role);

  const calculateGrossSalary = (payslip) => {
    if (payslip.payslip_type === '13_FIRST_HALF' || payslip.payslip_type === '13_SECOND_HALF' || payslip.payslip_type === '13_SINGLE_PARCEL') {
      return Number(payslip.regular_salary_weekday || payslip.total_salary || 0);
    }
    const extraAdvances = Number(payslip.extra_advances_total || 0);
    return (
      Number(payslip.regular_salary_weekday || 0) +
      Number(payslip.regular_salary_weekend || 0) +
      Number(payslip.fixed_saturday_amount || 0) +
      Number(payslip.overtime_salary_weekday || 0) +
      Number(payslip.overtime_salary_weekend || 0) +
      Number(payslip.holiday_bonus_amount || 0) +
      Number(payslip.extra_monthly_amount || 0) +
      extraAdvances
    );
  };

  const calculateNetSalary = (payslip) => {
    if (payslip.payslip_type === '13_FIRST_HALF' || payslip.payslip_type === '13_SECOND_HALF' || payslip.payslip_type === '13_SINGLE_PARCEL') {
      return Number(payslip.total_salary || 0);
    }
    const gross = calculateGrossSalary(payslip);
    const inss = Number(payslip.inss_deduction || 0);
    const advance = Number(payslip.advance_deduction || 0);
    const diverseExpenses = Number(payslip.diverse_expenses_total || 0);
    return gross - inss - advance - diverseExpenses;
  };

  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem('payslips-filters');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          selectedYear: parsed.selectedYear || new Date().getFullYear(),
          selectedMonth: parsed.selectedMonth || new Date().getMonth() + 1,
          selectedUserId: parsed.selectedUserId || '',
          selectedStatus: parsed.selectedStatus || '',
          selectedLimit: parsed.selectedLimit || 10,
        };
      }
    } catch (e) { }
    return {
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      selectedUserId: '',
      selectedStatus: '',
      selectedLimit: 10,
    };
  };

  const [activeTab, setActiveTab] = useState('REGULAR'); // 'REGULAR' ou '13TH'
  const [selectedYear, setSelectedYear] = useState(getSavedFilters().selectedYear);
  const [selectedMonth, setSelectedMonth] = useState(getSavedFilters().selectedMonth);
  const [selectedUserId, setSelectedUserId] = useState(getSavedFilters().selectedUserId);
  const [selectedStatus, setSelectedStatus] = useState(getSavedFilters().selectedStatus);
  const [selectedLimit, setSelectedLimit] = useState(getSavedFilters().selectedLimit);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewPayslipId, setViewPayslipId] = useState(null);
  const [selectedPayslips, setSelectedPayslips] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePendingConfirm, setDeletePendingConfirm] = useState(null);
  const [bulkGenerateConfirm, setBulkGenerateConfirm] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Modal de pagamento e comprovante manual
  const [paymentModal, setPaymentModal] = useState({
    open: false,
    payslip: null,
    isSplit: false,
    paymentMethod: 'PIX',
    splits: [
      { method: 'PIX', amount: '' },
      { method: 'DINHEIRO', amount: '' }
    ],
    paymentDate: new Date().toISOString().split('T')[0],
    receiptFile: null,
    paymentNotes: ''
  });

  const [paymentSuccessModal, setPaymentSuccessModal] = useState({
    open: false,
    payslip: null
  });

  useEffect(() => {
    localStorage.setItem('payslips-filters', JSON.stringify({
      selectedYear,
      selectedMonth,
      selectedUserId,
      selectedStatus,
      selectedLimit,
    }));
  }, [selectedYear, selectedMonth, selectedUserId, selectedStatus, selectedLimit]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedMonth, selectedUserId, selectedStatus, activeTab]);

  const { data: usersData } = useQuery({
    queryKey: ['users-for-payslips'],
    queryFn: () => userService.getUsers({ limit: 100, active: true }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    return [];
  }, [usersData]);

  const { data: payslipsData, refetch } = useQuery({
    queryKey: ['payslips', activeTab, selectedYear, activeTab === '13TH' ? 12 : selectedMonth, selectedUserId, selectedStatus, currentPage, selectedLimit],
    queryFn: () => payslipService.getPayslips({
      year: selectedYear,
      month: activeTab === '13TH' ? undefined : selectedMonth,
      payslipType: activeTab === '13TH' ? '13TH' : 'REGULAR',
      userId: selectedUserId || undefined,
      status: selectedStatus || undefined,
      page: currentPage,
      limit: selectedLimit,
    }),
    staleTime: 0,
  });

  const payslips = useMemo(() => {
    if (!payslipsData?.data?.data) return [];
    return Array.isArray(payslipsData.data.data) ? payslipsData.data.data : [];
  }, [payslipsData]);

  const pagination = useMemo(() => {
    return payslipsData?.data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 };
  }, [payslipsData]);

  const generateMutation = useMutation({
    mutationFn: ({ userId, year, month }) => payslipService.generatePreview({ userId, year, month }),
    onSuccess: () => {
      toast.success('Previa de holerite gerada com sucesso!');
      queryClient.invalidateQueries(['payslips']);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao gerar previa');
    },
  });

  const generateBulkMutation = useMutation({
    mutationFn: (data) => payslipService.generateBulkPreview(data),
    onSuccess: (data) => {
      const { results, errors } = data.data.data;
      const successCount = results.filter(r => r.status === 'success').length;
      if (successCount > 0) {
        toast.success(`${successCount} previas geradas com sucesso!`);
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} erros ao gerar previas.`);
      }
      queryClient.invalidateQueries(['payslips']);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao gerar previas em massa');
    }
  });

  // Mutações específicas para geração do 13º Salário
  const generate13thMutation = useMutation({
    mutationFn: ({ userId, year, parcel = 0 }) => payslipService.generateThirteenth({ userId, year, parcel }),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Holerite de 13º Salário gerado com sucesso!');
      queryClient.invalidateQueries(['payslips']);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao gerar 13º Salário');
    }
  });

  const generate13thBulkMutation = useMutation({
    mutationFn: ({ year, parcel = 0 }) => payslipService.generateThirteenthBulk({ year, parcel }),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Geração em lote de 13º Salário concluída!');
      queryClient.invalidateQueries(['payslips']);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao gerar 13º em lote');
    }
  });

  const approveMutation = useMutation({
    mutationFn: ({ payslipId, data }) => payslipService.approvePayslip(payslipId, data),
    onSuccess: () => {
      toast.success('Holerite aprovado com sucesso!');
      queryClient.invalidateQueries(['payslips']);
      queryClient.invalidateQueries(['finance-stats']);
      queryClient.invalidateQueries(['finance-expenses']);
      queryClient.invalidateQueries(['finance-forecast']);
      queryClient.invalidateQueries(['finance-forecasted-expenses']);
      refetch();
      setViewPayslipId(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao aprovar holerite');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ payslipId, reason }) => payslipService.rejectPayslip(payslipId, { rejectionReason: reason }),
    onSuccess: () => {
      toast.success('Holerite rejeitado.');
      queryClient.invalidateQueries(['payslips']);
      refetch();
      setViewPayslipId(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao rejeitar holerite');
    },
  });

  const recordPaymentMutation = useMutation({
    mutationFn: ({ payslipId, formData }) => payslipService.recordPayslipPayment(payslipId, formData),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Pagamento e comprovante registrados com sucesso!');
      queryClient.invalidateQueries(['payslips']);
      queryClient.invalidateQueries(['cashbox-transactions']);
      queryClient.invalidateQueries(['company-cashbox-balance']);
      queryClient.invalidateQueries(['finance-stats']);
      refetch();
      
      const savedPayslip = res?.data?.data || paymentModal.payslip;
      setPaymentModal({ open: false, payslip: null, isSplit: false, paymentMethod: 'PIX', splits: [], paymentDate: '', receiptFile: null, paymentNotes: '' });
      
      // Abrir modal de sucesso com opção imediata de baixar a Via de Recibo Assinada
      if (savedPayslip) {
        setPaymentSuccessModal({ open: true, payslip: savedPayslip });
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Erro ao registrar pagamento');
    }
  });

  const handleOpenPaymentModal = (payslip) => {
    if (!isManager) {
      toast.error('Apenas a Gerência/Administração pode registrar e editar formas de pagamento.');
      return;
    }

    if (payslip.status !== 'APPROVED') {
      toast.error('Este holerite precisa ser aprovado pela gerência antes de registrar o pagamento.');
      return;
    }

    const netSalary = calculateNetSalary(payslip);
    const existingSplits = Array.isArray(payslip.payment_splits) && payslip.payment_splits.length > 0
      ? payslip.payment_splits
      : [
          { method: 'PIX', amount: (netSalary / 2).toFixed(2) },
          { method: 'DINHEIRO', amount: (netSalary / 2).toFixed(2) }
        ];

    setPaymentModal({
      open: true,
      payslip,
      isSplit: payslip.payment_method === 'FRACIONADO' || (Array.isArray(payslip.payment_splits) && payslip.payment_splits.length > 1),
      paymentMethod: (payslip.payment_method && payslip.payment_method !== 'FRACIONADO') ? payslip.payment_method : 'PIX',
      splits: existingSplits,
      paymentDate: payslip.paid_at ? new Date(payslip.paid_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      receiptFile: null,
      paymentNotes: payslip.payment_notes || ''
    });
  };

  const handleSavePaymentModal = (e) => {
    e.preventDefault();
    if (!paymentModal.payslip) return;

    const netSalary = calculateNetSalary(paymentModal.payslip);
    const formData = new FormData();
    formData.append('payment_date', paymentModal.paymentDate);
    formData.append('payment_notes', paymentModal.paymentNotes || '');

    if (paymentModal.isSplit) {
      // Validar se soma dos splits bate com o total líquido
      const totalSplits = paymentModal.splits.reduce((acc, s) => acc + (parseFloat(s.amount) || 0), 0);
      const diff = Math.abs(totalSplits - netSalary);
      if (diff > 0.05) {
        toast.error(`A soma das partes (R$ ${totalSplits.toFixed(2)}) deve ser igual ao valor líquido do holerite (R$ ${netSalary.toFixed(2)})`);
        return;
      }
      formData.append('payment_method', 'FRACIONADO');
      formData.append('payment_splits', JSON.stringify(paymentModal.splits.map(s => ({
        method: s.method,
        amount: parseFloat(s.amount) || 0
      }))));
    } else {
      formData.append('payment_method', paymentModal.paymentMethod);
      formData.append('payment_splits', JSON.stringify([]));
    }

    if (paymentModal.receiptFile) {
      formData.append('receipt', paymentModal.receiptFile);
    }

    recordPaymentMutation.mutate({
      payslipId: paymentModal.payslip.id,
      formData
    });
  };

  const handleDownloadReceipt = async (payslip) => {
    try {
      const fullRes = await payslipService.getPayslip(payslip.id);
      const fullData = fullRes?.data?.data || payslip;
      await pdfService.generatePayslipReceiptPDF(fullData);
      toast.success('Via de Recibo e Quitação baixada com sucesso!');
    } catch (err) {
      console.error('Erro ao baixar recibo:', err);
      toast.error('Erro ao gerar via de recibo');
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (payslipId) => payslipService.deletePayslip(payslipId),
    onSuccess: (data) => {
      toast.success(data.data.message || 'Holerite deletado com sucesso!');
      queryClient.invalidateQueries(['payslips']);
      queryClient.invalidateQueries(['finance-payroll']);
      queryClient.invalidateQueries(['finance-stats']);
      queryClient.invalidateQueries(['finance-expenses']);
      queryClient.invalidateQueries(['finance-forecast']);
      queryClient.invalidateQueries(['finance-forecasted-expenses']);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar holerite');
    },
  });

  const approveBulkMutation = useMutation({
    mutationFn: (payslipIds) => payslipService.approveBulk({ payslipIds }),
    onSuccess: (data) => {
      const { results, errors } = data.data.data;
      const successCount = results.filter(r => r.status === 'success').length;
      if (successCount > 0) {
        toast.success(`${successCount} holerites aprovados com sucesso!`);
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} erros ao aprovar holerites.`);
        console.error('Erros na aprovacao em massa:', errors);
      }
      queryClient.invalidateQueries(['payslips']);
      queryClient.invalidateQueries(['finance-stats']);
      setSelectedPayslips([]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro na aprovacao em massa');
    }
  });

  const toggleSelection = (id) => {
    setSelectedPayslips(prev =>
      prev.includes(id) ? prev.filter(pId => pId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const pendingIds = payslips
      .filter(p => p.status === 'PENDING_APPROVAL')
      .map(p => p.id);
    const allSelected = pendingIds.length > 0 && pendingIds.every(id => selectedPayslips.includes(id));
    if (allSelected) {
      setSelectedPayslips([]);
    } else {
      setSelectedPayslips(pendingIds);
    }
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const handleDelete = (payslip) => {
    if (payslip.status === 'APPROVED') {
      setDeleteConfirm({ payslip, amount: payslip.total_salary });
    } else {
      setDeletePendingConfirm({ payslip });
    }
  };

  const handleBulkDownload = async () => {
    if (selectedPayslips.length === 0) return;

    let successCount = 0;
    let errorCount = 0;

    for (const payslipId of selectedPayslips) {
      try {
        const fullPayslip = await payslipService.getPayslip(payslipId);
        if (fullPayslip?.data?.data) {
          pdfService.generatePayslipPDF(fullPayslip.data.data);
          successCount++;
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
        console.error(`Erro ao gerar PDF para holerite ${payslipId}:`, error);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} PDF(s) gerado(s) com sucesso!`);
    }
    if (errorCount > 0) {
      toast.error(`Erro ao gerar ${errorCount} PDF(s)`);
    }
  };

  const handleExportAccounting = async () => {
    setIsExporting(true);
    try {
      const response = await payslipService.exportAccounting({
        year: selectedYear,
        month: selectedMonth,
        status: selectedStatus || undefined,
        format: 'csv'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `folha_contabil_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Dados contábeis exportados em CSV com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar contábil:', error);
      toast.error('Erro ao exportar dados contábeis da folha.');
    } finally {
      setIsExporting(false);
    }
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm.payslip.id);
      setDeleteConfirm(null);
    }
  };

  const getStatusBadge = (status, payslip = null) => {
    if ((payslip && payslip.paid_at) || status === 'PAID') {
      return 'bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold';
    }
    const badges = {
      DRAFT: 'bg-gray-100 text-gray-800',
      PENDING_APPROVAL: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-blue-100 text-blue-800 border border-blue-200',
      REJECTED: 'bg-red-100 text-red-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status, payslip = null) => {
    if ((payslip && payslip.paid_at) || status === 'PAID') {
      return 'Pago ✓';
    }
    const labels = {
      DRAFT: 'Rascunho',
      PENDING_APPROVAL: 'Aguardando Aprovação',
      APPROVED: 'Aprovado (Aguardando Pagamento)',
      REJECTED: 'Rejeitado',
    };
    return labels[status] || status;
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-900">Gestao de Holerites</h1>
          <p className="text-xs sm:text-sm text-gray-600">
            {activeTab === 'REGULAR'
              ? 'Gerencie e aprove holerites mensais dos funcionários'
              : 'Gerencie e aprove os holerites oficiais de 13º Salário (Cota Única e Parcelas)'}
          </p>
        </div>

        {/* Abas: Folha Mensal vs 13º Salário */}
        <div className="flex bg-gray-100 p-1 rounded-xl border border-gray-200 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setActiveTab('REGULAR')}
            className={`px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition-all ${
              activeTab === 'REGULAR'
                ? 'bg-white text-blue-700 shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Folha Mensal</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('13TH')}
            className={`px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition-all ${
              activeTab === '13TH'
                ? 'bg-white text-amber-700 shadow-xs'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>🎁 13º Salário</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Ano</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          {activeTab === 'REGULAR' ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Mes</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {monthNames.map((name, index) => (
                  <option key={index + 1} value={index + 1}>{name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Competência</label>
              <div className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-amber-800 font-semibold">
                Dezembro (Cota Única / 13º)
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="DRAFT">Rascunho</option>
              <option value="PENDING_APPROVAL">Aguardando Aprovação</option>
              <option value="APPROVED_UNPAID">Aprovado (Pendente Pagto)</option>
              <option value="PAID">Pago ✓</option>
              <option value="REJECTED">Rejeitado</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Funcionario</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Por pagina</label>
            <select
              value={selectedLimit}
              onChange={(e) => {
                setSelectedLimit(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {activeTab === 'REGULAR' ? (
            <>
              <button
                onClick={() => {
                  if (!selectedUserId) {
                    toast.error('Selecione um funcionario para gerar previa');
                    return;
                  }
                  generateMutation.mutate({
                    userId: selectedUserId,
                    year: selectedYear,
                    month: selectedMonth,
                  });
                }}
                disabled={generateMutation.isLoading || !selectedUserId}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Plus className="w-3.5 h-3.5" />
                {generateMutation.isLoading ? 'Gerando...' : 'Gerar Individual'}
              </button>
              <button
                onClick={() => setBulkGenerateConfirm(true)}
                disabled={generateBulkMutation.isLoading || users.length === 0}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                <FileText className="w-3.5 h-3.5" />
                {generateBulkMutation.isLoading ? 'Gerando...' : 'Gerar Todos'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  if (!selectedUserId) {
                    toast.error('Selecione um funcionário para gerar o 13º Salário');
                    return;
                  }
                  generate13thMutation.mutate({
                    userId: selectedUserId,
                    year: selectedYear,
                    parcel: 0 // Cota Única Integral
                  });
                }}
                disabled={generate13thMutation.isPending || !selectedUserId}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 text-sm bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors shadow-xs"
              >
                <Plus className="w-3.5 h-3.5" />
                {generate13thMutation.isPending ? 'Gerando...' : 'Gerar 13º Individual (Integral)'}
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Deseja gerar os holerites de 13º Salário (Cota Única Integral) de ${selectedYear} para todos os colaboradores ativos?`)) {
                    generate13thBulkMutation.mutate({ year: selectedYear, parcel: 0 });
                  }
                }}
                disabled={generate13thBulkMutation.isPending || users.length === 0}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 text-sm bg-amber-700 text-white font-semibold rounded-lg hover:bg-amber-800 disabled:opacity-50 transition-colors shadow-xs"
              >
                <FileText className="w-3.5 h-3.5" />
                {generate13thBulkMutation.isPending ? 'Gerando...' : 'Gerar 13º Todos (Cota Única)'}
              </button>
            </>
          )}
          <button
            onClick={handleExportAccounting}
            disabled={isExporting}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            title="Exportar lançamentos e encargos da folha para contabilidade e eSocial (CSV)"
          >
            <Download className="w-3.5 h-3.5" />
            {isExporting ? 'Exportando...' : 'Exportar Contábil (CSV)'}
          </button>
        </div>
      </div>

      {selectedPayslips.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-3">
          <div className="flex items-center justify-between mb-2 sm:mb-0 sm:gap-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                {selectedPayslips.length}
              </span>
              <span className="text-blue-700 text-xs sm:text-sm font-medium">
                holerite{selectedPayslips.length > 1 ? 's' : ''} selecionad{selectedPayslips.length > 1 ? 'os' : 'o'}
              </span>
            </div>
            <button
              onClick={() => setSelectedPayslips([])}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Limpar
            </button>
          </div>
          <div className="flex gap-2 mt-2 sm:mt-0">
            <button
              onClick={handleBulkDownload}
              className="flex-1 sm:flex-none px-3 py-1.5 text-xs bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center justify-center gap-1.5"
            >
              <Download className="w-3 h-3" />
              <span>PDFs</span>
            </button>
            <button
              onClick={() => approveBulkMutation.mutate(selectedPayslips)}
              disabled={approveBulkMutation.isLoading}
              className="flex-1 sm:flex-none px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              <CheckCircle className="w-3 h-3" />
              <span>{approveBulkMutation.isLoading ? '...' : 'Aprovar'}</span>
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Mobile: Card list */}
        <div className="sm:hidden divide-y divide-gray-100">
          {payslips.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">
              Nenhum holerite encontrado para o periodo selecionado.
            </div>
          ) : (
            payslips.map((payslip) => {
              const gross = calculateGrossSalary(payslip);
              const net = calculateNetSalary(payslip);
              return (
                <div key={payslip.id} className={`p-3 ${payslip.status === 'PENDING_APPROVAL' && selectedPayslips.includes(payslip.id) ? 'bg-blue-50' : 'bg-white'}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {payslip.status === 'PENDING_APPROVAL' && (
                          <input
                            type="checkbox"
                            checked={selectedPayslips.includes(payslip.id)}
                            onChange={() => toggleSelection(payslip.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                          />
                        )}
                        <p className="font-semibold text-gray-900 text-sm truncate">{payslip.full_name}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {payslip.payslip_type === '13_SINGLE_PARCEL'
                          ? `13º Salário Integral (${payslip.ref_year})`
                          : payslip.payslip_type === '13_FIRST_HALF'
                          ? `13º Salário 1ª Parcela (${payslip.ref_year})`
                          : payslip.payslip_type === '13_SECOND_HALF'
                          ? `13º Salário 2ª Parcela (${payslip.ref_year})`
                          : `${monthNames[payslip.ref_month - 1]}/${payslip.ref_year}`}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full flex-shrink-0 ${getStatusBadge(payslip.status, payslip)}`}>
                      {payslip.paid_at && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                      {getStatusLabel(payslip.status, payslip)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="text-[10px] text-gray-500 mb-0.5">Total</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {gross.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2">
                      <p className="text-[10px] text-gray-500 mb-0.5">Liquido</p>
                      <p className="text-sm font-semibold text-green-700">
                        {net.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                    {payslip.status === 'APPROVED' ? (
                      <span className="text-[10px] text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Aprovado ({payslip.total_managers})
                      </span>
                    ) : (
                      <>
                        {payslip.approvals_list?.slice(0, 2).map(approval => (
                          <span key={approval.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800">
                            <Check className="w-2.5 h-2.5 mr-0.5" />
                            {approval.name.split(' ')[0]}
                          </span>
                        ))}
                        {payslip.pending_approvals?.slice(0, 1).map(pending => (
                          <span key={pending.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800">
                            <Clock className="w-2.5 h-2.5 mr-0.5" />
                            {pending.name.split(' ')[0]}
                          </span>
                        ))}
                      </>
                    )}
                  </div>

                  <div className="flex gap-1.5 flex-wrap">
                    <button
                      onClick={() => handleOpenPaymentModal(payslip)}
                      disabled={!isManager || payslip.status !== 'APPROVED'}
                      className={`py-1.5 px-2 text-xs rounded-lg flex items-center justify-center gap-1 font-semibold border transition-colors ${
                        payslip.paid_at
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200'
                          : payslip.status === 'APPROVED'
                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200'
                            : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                      }`}
                      title={
                        !isManager
                          ? 'Apenas a Gerência tem permissão para registrar pagamento'
                          : payslip.status !== 'APPROVED'
                            ? 'Aprovação pendente antes do pagamento'
                            : payslip.paid_at
                              ? 'Pagamento registrado (clique para ver/editar)'
                              : 'Registrar quitação/pagamento deste holerite'
                      }
                    >
                      <CreditCard className="w-3.5 h-3.5 text-emerald-600" />
                      <span>{payslip.paid_at ? (payslip.payment_method || 'Pago') : 'Pagar'}</span>
                      {payslip.receipt_file_url && <Paperclip className="w-3 h-3 text-emerald-600" />}
                    </button>
                    {(payslip.paid_at || payslip.payment_method) && (
                      <button
                        onClick={() => handleDownloadReceipt(payslip)}
                        className="py-1.5 px-2 text-xs bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-lg flex items-center justify-center gap-1 font-semibold border border-purple-200"
                        title="Baixar Via de Recibo assinada pelo colaborador com discriminação do pagamento"
                      >
                        <FileCheck className="w-3.5 h-3.5 text-purple-600" />
                        <span>Recibo</span>
                      </button>
                    )}
                    <button
                      onClick={() => {
                        payslipService.getPayslip(payslip.id).then((fullPayslip) => {
                          if (fullPayslip?.data?.data) {
                            pdfService.generatePayslipPDF(fullPayslip.data.data);
                            toast.success('PDF gerado!');
                          }
                        }).catch(() => toast.error('Erro ao gerar PDF'));
                      }}
                      className="flex-1 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-1 min-w-[70px]"
                    >
                      <Download className="w-3 h-3" />
                      PDF
                    </button>
                    {payslip.status === 'PENDING_APPROVAL' && (
                      <>
                        <button
                          onClick={() => approveMutation.mutate({ payslipId: payslip.id, data: {} })}
                          className="flex-1 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-1"
                        >
                          <CheckCircle className="w-3 h-3" />
                          Aprovar
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Motivo da rejeicao:');
                            if (reason) rejectMutation.mutate({ payslipId: payslip.id, reason });
                          }}
                          className="py-1.5 px-2 text-xs text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(payslip)}
                      className="py-1.5 px-2 text-xs text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Desktop: Table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={payslips.filter(p => p.status === 'PENDING_APPROVAL').length > 0 && payslips.filter(p => p.status === 'PENDING_APPROVAL').every(p => selectedPayslips.includes(p.id))}
                    onChange={toggleSelectAll}
                    disabled={payslips.filter(p => p.status === 'PENDING_APPROVAL').length === 0}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Acoes</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Funcionario</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mes/Ano</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Forma Pagto</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Liquido</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Aprovacoes</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {payslips.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-3 py-6 text-center text-gray-500 text-sm">
                    Nenhum holerite encontrado para o periodo selecionado.
                  </td>
                </tr>
              ) : (
                payslips.map((payslip) => (
                  <tr key={payslip.id} className={`hover:bg-gray-50 text-xs ${payslip.status === 'PENDING_APPROVAL' && selectedPayslips.includes(payslip.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedPayslips.includes(payslip.id)}
                        onChange={() => toggleSelection(payslip.id)}
                        disabled={payslip.status !== 'PENDING_APPROVAL'}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleOpenPaymentModal(payslip)}
                          disabled={!isManager || payslip.status !== 'APPROVED'}
                          className={`p-1 rounded transition-colors ${
                            payslip.paid_at
                              ? 'text-emerald-700 bg-emerald-100 hover:bg-emerald-200'
                              : payslip.status === 'APPROVED'
                                ? 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                                : 'text-gray-300 cursor-not-allowed opacity-50'
                          }`}
                          title={
                            !isManager
                              ? 'Apenas a Gerência tem permissão para registrar pagamento'
                              : payslip.status !== 'APPROVED'
                                ? 'Aprovação pendente antes do pagamento'
                                : payslip.paid_at
                                  ? 'Pagamento registrado (clique para ver/editar)'
                                  : 'Registrar quitação/pagamento deste holerite'
                          }
                        >
                          <CreditCard className="w-3.5 h-3.5" />
                        </button>
                        {(payslip.paid_at || payslip.payment_method) && (
                          <button
                            onClick={() => handleDownloadReceipt(payslip)}
                            className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded"
                            title="Baixar Via de Recibo (com Assinatura)"
                          >
                            <FileCheck className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            payslipService.getPayslip(payslip.id).then((fullPayslip) => {
                              if (fullPayslip?.data?.data) {
                                pdfService.generatePayslipPDF(fullPayslip.data.data);
                                toast.success('PDF do holerite gerado com sucesso!');
                              } else {
                                toast.error('Erro ao buscar dados completos do holerite');
                              }
                            }).catch(() => {
                              toast.error('Erro ao gerar PDF do holerite');
                            });
                          }}
                          className="p-1 text-gray-500 hover:text-gray-700"
                          title="Baixar PDF"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {payslip.status === 'PENDING_APPROVAL' && (
                          <>
                            <button
                              onClick={() => approveMutation.mutate({ payslipId: payslip.id, data: {} })}
                              className="p-1 text-green-600 hover:text-green-700"
                              disabled={approveMutation.isLoading}
                              title="Aprovar"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                const reason = prompt('Motivo da rejeicao:');
                                if (reason) {
                                  rejectMutation.mutate({ payslipId: payslip.id, reason });
                                }
                              }}
                              className="p-1 text-red-600 hover:text-red-700"
                              disabled={rejectMutation.isLoading}
                              title="Rejeitar"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(payslip)}
                          className="p-1 text-red-500 hover:text-red-700"
                          disabled={deleteMutation.isLoading}
                          title="Deletar holerite"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{payslip.full_name}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-900 font-medium">
                      {payslip.payslip_type === '13_SINGLE_PARCEL'
                        ? <span className="text-amber-800 font-bold">13º Integral ({payslip.ref_year})</span>
                        : payslip.payslip_type === '13_FIRST_HALF'
                        ? <span className="text-amber-700 font-semibold">13º 1ª Parc. ({payslip.ref_year})</span>
                        : payslip.payslip_type === '13_SECOND_HALF'
                        ? <span className="text-amber-700 font-semibold">13º 2ª Parc. ({payslip.ref_year})</span>
                        : `${monthNames[payslip.ref_month - 1]}/${payslip.ref_year}`}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {payslip.payment_method === 'FRACIONADO' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200" title={Array.isArray(payslip.payment_splits) ? payslip.payment_splits.map(s => `${s.method}: R$ ${Number(s.amount).toFixed(2)}`).join(' + ') : 'Fracionado'}>
                          <CreditCard className="w-3 h-3 text-purple-600" />
                          Fracionado ({payslip.payment_splits?.length || 2}x)
                        </span>
                      ) : payslip.payment_method ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${
                          payslip.payment_method === 'PIX' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                          payslip.payment_method === 'DINHEIRO' ? 'bg-green-100 text-green-800 border-green-200' :
                          payslip.payment_method === 'TRANSFERENCIA' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                          'bg-gray-100 text-gray-800 border-gray-200'
                        }`}>
                          <CreditCard className="w-3 h-3" />
                          {payslip.payment_method}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic text-[11px]">A definir</span>
                      )}
                      {payslip.receipt_file_url && (
                        <a
                          href={payslip.receipt_file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-1 inline-flex text-blue-600 hover:text-blue-800"
                          title={`Ver comprovante anexo: ${payslip.receipt_file_name || 'Comprovante'}`}
                        >
                          <Paperclip className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right font-medium text-gray-900">
                      {calculateGrossSalary(payslip).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right font-medium text-green-700">
                      {calculateNetSalary(payslip).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadge(payslip.status, payslip)}`}>
                        {payslip.paid_at && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                        {getStatusLabel(payslip.status, payslip)}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {payslip.status === 'APPROVED' ? (
                        <span className="text-xs text-green-600 font-medium">OK ({payslip.total_managers})</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {payslip.approvals_list?.slice(0, 3).map(approval => (
                            <span key={approval.id} className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" title={approval.name}>
                              <Check className="w-2.5 h-2.5 mr-0.5" />
                              {approval.name.split(' ')[0]}
                            </span>
                          ))}
                          {payslip.pending_approvals?.slice(0, 2).map(pending => (
                            <span key={pending.id} className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800" title={pending.name}>
                              <Clock className="w-2.5 h-2.5 mr-0.5" />
                              {pending.name.split(' ')[0]}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 0 && (
          <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="text-xs text-gray-700">
              Mostrando <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>-<span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> de{' '}
              <span className="font-medium">{pagination.total}</span>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={pagination.page <= 1}
                className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Ant
              </button>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-2 py-1 text-xs border rounded ${
                      pagination.page === pageNum
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Prox
              </button>
            </div>
          </div>
        )}
      </div>

      {viewPayslipId && (
        <PayslipDetailWrapper
          payslipId={viewPayslipId}
          onClose={() => setViewPayslipId(null)}
          onApprove={(data) => approveMutation.mutate({ payslipId: viewPayslipId, data })}
          onReject={(reason) => rejectMutation.mutate({ payslipId: viewPayslipId, reason })}
        />
      )}

      {/* Modal de Registro de Pagamento, Fracionamento e Comprovante */}
      {paymentModal.open && paymentModal.payslip && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-6">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity" onClick={() => setPaymentModal({ open: false, payslip: null, isSplit: false, paymentMethod: 'PIX', splits: [], paymentDate: '', receiptFile: null, paymentNotes: '' })}></div>
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
              <div className="px-6 py-4 bg-gradient-to-r from-emerald-700 to-green-700 text-white flex justify-between items-center">
                <div>
                  <h3 className="text-base sm:text-lg font-bold flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-emerald-200" />
                    <span>Pagamento de Holerite</span>
                  </h3>
                  <p className="text-xs text-emerald-100 mt-0.5">
                    {paymentModal.payslip.full_name} • {monthNames[paymentModal.payslip.ref_month - 1]}/{paymentModal.payslip.ref_year}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPaymentModal({ open: false, payslip: null, isSplit: false, paymentMethod: 'PIX', splits: [], paymentDate: '', receiptFile: null, paymentNotes: '' })}
                  className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSavePaymentModal} className="p-6 space-y-4">
                {/* Resumo do Valor Líquido */}
                <div className="bg-emerald-50 rounded-xl p-3.5 border border-emerald-200 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold text-emerald-800 uppercase block">Salário Líquido a Pagar</span>
                    <span className="text-xl font-extrabold text-emerald-950">
                      {calculateNetSalary(paymentModal.payslip).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </div>
                  <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-white text-emerald-800 border border-emerald-300">
                    Status: {getStatusLabel(paymentModal.payslip.status)}
                  </span>
                </div>

                {/* Tipo de Pagamento: Integral vs Fracionado */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                    Tipo de Pagamento
                  </label>
                  <div className="grid grid-cols-2 gap-2 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => setPaymentModal(prev => ({ ...prev, isSplit: false }))}
                      className={`p-2.5 rounded-xl border text-center transition-all ${
                        !paymentModal.isSplit
                          ? 'bg-emerald-700 text-white border-emerald-700 shadow-xs'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div>Pagamento Único</div>
                      <div className="text-[10px] opacity-80 mt-0.5">100% em uma forma</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const net = calculateNetSalary(paymentModal.payslip);
                        setPaymentModal(prev => ({
                          ...prev,
                          isSplit: true,
                          splits: prev.splits?.length > 0 ? prev.splits : [
                            { method: 'PIX', amount: (net / 2).toFixed(2) },
                            { method: 'DINHEIRO', amount: (net / 2).toFixed(2) }
                          ]
                        }));
                      }}
                      className={`p-2.5 rounded-xl border text-center transition-all ${
                        paymentModal.isSplit
                          ? 'bg-purple-700 text-white border-purple-700 shadow-xs'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div>Fracionado (Múltiplo)</div>
                      <div className="text-[10px] opacity-80 mt-0.5">Ex: PIX + Dinheiro</div>
                    </button>
                  </div>
                </div>

                {/* Pagamento Único */}
                {!paymentModal.isSplit && (
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      Forma de Pagamento *
                    </label>
                    <select
                      value={paymentModal.paymentMethod}
                      onChange={(e) => setPaymentModal(prev => ({ ...prev, paymentMethod: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm font-semibold bg-white text-gray-800 focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="PIX">⚡ PIX</option>
                      <option value="DINHEIRO">💵 Dinheiro em Espécie</option>
                      <option value="TRANSFERENCIA">🏦 Transferência Bancária</option>
                      <option value="BALANCO_CAIXA">💼 Balanço de Caixa</option>
                      <option value="CHEQUE">📝 Cheque</option>
                      <option value="CARTAO">💳 Cartão</option>
                    </select>
                  </div>
                )}

                {/* Pagamento Fracionado */}
                {paymentModal.isSplit && (
                  <div className="bg-purple-50/60 rounded-xl p-3 border border-purple-200 space-y-2.5">
                    <div className="flex items-center justify-between text-xs font-bold text-purple-900">
                      <span>Divisão dos Valores</span>
                      <button
                        type="button"
                        onClick={() => setPaymentModal(prev => ({
                          ...prev,
                          splits: [...prev.splits, { method: 'PIX', amount: '' }]
                        }))}
                        className="text-purple-700 hover:text-purple-900 text-xs font-bold flex items-center gap-1"
                      >
                        <Plus className="w-3.5 h-3.5" /> Adicionar Parte
                      </button>
                    </div>

                    {paymentModal.splits.map((s, sIdx) => (
                      <div key={sIdx} className="flex items-center gap-2">
                        <select
                          value={s.method}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPaymentModal(prev => {
                              const next = [...prev.splits];
                              next[sIdx].method = val;
                              return { ...prev, splits: next };
                            });
                          }}
                          className="w-1/2 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold bg-white"
                        >
                          <option value="PIX">PIX</option>
                          <option value="DINHEIRO">Dinheiro</option>
                          <option value="TRANSFERENCIA">Transferência</option>
                          <option value="BALANCO_CAIXA">Balanço Caixa</option>
                          <option value="CHEQUE">Cheque</option>
                          <option value="CARTAO">Cartão</option>
                        </select>

                        <div className="relative w-1/2">
                          <span className="absolute left-2.5 top-1.5 text-gray-400 font-bold text-xs">R$</span>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0,00"
                            value={s.amount}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPaymentModal(prev => {
                                const next = [...prev.splits];
                                next[sIdx].amount = val;
                                return { ...prev, splits: next };
                              });
                            }}
                            className="w-full pl-7 pr-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-bold text-gray-900 bg-white"
                          />
                        </div>

                        {paymentModal.splits.length > 2 && (
                          <button
                            type="button"
                            onClick={() => {
                              setPaymentModal(prev => ({
                                ...prev,
                                splits: prev.splits.filter((_, i) => i !== sIdx)
                              }));
                            }}
                            className="p-1 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}

                    {/* Verificador de Soma */}
                    {(() => {
                      const net = calculateNetSalary(paymentModal.payslip);
                      const sumSplits = paymentModal.splits.reduce((sum, sp) => sum + (parseFloat(sp.amount) || 0), 0);
                      const diff = Number((net - sumSplits).toFixed(2));
                      return (
                        <div className="pt-1.5 border-t border-purple-200 flex justify-between text-[11px] font-semibold">
                          <span className="text-gray-600">Soma das Partes: <strong>R$ {sumSplits.toFixed(2)}</strong></span>
                          {Math.abs(diff) > 0.05 ? (
                            <span className="text-red-600 font-bold">Faltam: R$ {diff.toFixed(2)}</span>
                          ) : (
                            <span className="text-emerald-700 font-bold flex items-center gap-1">
                              <Check className="w-3 h-3" /> Soma bate com o líquido!
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Data do Pagamento */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Data do Pagamento *
                  </label>
                  <input
                    type="date"
                    required
                    value={paymentModal.paymentDate}
                    onChange={(e) => setPaymentModal(prev => ({ ...prev, paymentDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs text-gray-900 focus:ring-2 focus:ring-emerald-500 bg-white"
                  />
                </div>

                {/* Comprovante / Recibo Assinado Atual (se já existir) */}
                {paymentModal.payslip.receipt_file_url && (
                  <div className="p-3 bg-emerald-50/70 rounded-xl border border-emerald-200 space-y-1.5 text-xs text-emerald-900">
                    <div className="flex items-center justify-between">
                      <span className="font-bold flex items-center gap-1.5 truncate">
                        <Paperclip className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                        <span className="truncate">{paymentModal.payslip.receipt_file_name || 'Comprovante / Recibo assinado já anexado'}</span>
                      </span>
                      <a
                        href={paymentModal.payslip.receipt_file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-700 hover:text-emerald-900 font-bold underline flex items-center gap-1 flex-shrink-0 ml-2 text-xs"
                      >
                        <ExternalLink className="w-3 h-3" /> Ver Arquivo
                      </a>
                    </div>
                    <p className="text-[10px] text-emerald-700 opacity-90">
                      Caso queira substituir, selecione um novo arquivo abaixo.
                    </p>
                  </div>
                )}

                {/* Anexo de Comprovante Bancário ou Recibo Assinado */}
                <div className="bg-gray-50/80 rounded-xl p-3 border border-dashed border-gray-300 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-bold text-gray-700">
                      Anexar Recibo Assinado ou Comprovante <span className="text-gray-400 font-normal">(Opcional)</span>
                    </label>
                    <span className="text-[10px] text-gray-500 font-medium">PDF, JPG, PNG</span>
                  </div>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) => setPaymentModal(prev => ({ ...prev, receiptFile: e.target.files[0] || null }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-xl text-xs text-gray-600 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-[11px] file:font-bold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 bg-white"
                  />
                  <p className="text-[10px] text-gray-500">
                    Dica: Você pode salvar o pagamento agora, baixar a <strong>Via de Recibo</strong> para assinatura mútua e anexar a via digitalizada aqui a qualquer momento.
                  </p>
                </div>

                {/* Observações */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Observações do Pagamento (Opcional)
                  </label>
                  <input
                    type="text"
                    value={paymentModal.paymentNotes}
                    onChange={(e) => setPaymentModal(prev => ({ ...prev, paymentNotes: e.target.value }))}
                    placeholder="Ex: Pago metade via Pix pessoal e restante em dinheiro no balcão..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs text-gray-900 focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {/* Botões de Ação */}
                <div className="flex gap-3 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setPaymentModal({ open: false, payslip: null, isSplit: false, paymentMethod: 'PIX', splits: [], paymentDate: '', receiptFile: null, paymentNotes: '' })}
                    className="flex-1 px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={recordPaymentMutation.isLoading}
                    className="flex-1 px-4 py-2 text-xs font-bold text-white bg-emerald-700 hover:bg-emerald-800 rounded-xl shadow-xs transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                    {recordPaymentMutation.isLoading ? 'Salvando...' : 'Salvar Pagamento'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sucesso Pós-Pagamento com Download de Recibo Assinado */}
      {paymentSuccessModal.open && paymentSuccessModal.payslip && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 backdrop-blur-xs"
              onClick={() => setPaymentSuccessModal({ open: false, payslip: null })}
            />

            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150 text-left">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Pagamento Registrado!</h3>
                    <p className="text-xs text-gray-500">Holerite pronto para quitação formal</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPaymentSuccessModal({ open: false, payslip: null })}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-3.5 space-y-2 text-xs border border-gray-200">
                <div className="flex justify-between">
                  <span className="text-gray-500">Colaborador:</span>
                  <span className="font-bold text-gray-900">{paymentSuccessModal.payslip.full_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Competência:</span>
                  <span className="font-medium text-gray-800">{paymentSuccessModal.payslip.ref_month}/{paymentSuccessModal.payslip.ref_year}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Modalidade:</span>
                  <span className="font-semibold text-indigo-700">
                    {paymentSuccessModal.payslip.payment_method === 'FRACIONADO'
                      ? '⚡ Fracionado (Múltiplas Formas)'
                      : `💳 Único (${paymentSuccessModal.payslip.payment_method || 'PIX'})`}
                  </span>
                </div>
                <div className="flex justify-between pt-1 border-t border-gray-200">
                  <span className="text-gray-600 font-semibold">Salário Líquido:</span>
                  <span className="font-bold text-emerald-700 text-sm">
                    {Number(paymentSuccessModal.payslip.total_salary || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </span>
                </div>
              </div>

              <div className="space-y-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => handleDownloadReceipt(paymentSuccessModal.payslip)}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl shadow-md transition-all text-xs"
                >
                  <FileCheck className="w-4 h-4" />
                  <span>1. Baixar Via de Recibo (para Assinatura)</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const payslipToEdit = paymentSuccessModal.payslip;
                    setPaymentSuccessModal({ open: false, payslip: null });
                    handleOpenPaymentModal(payslipToEdit);
                  }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold rounded-xl transition-all text-xs"
                >
                  <Paperclip className="w-4 h-4 text-emerald-600" />
                  <span>2. Anexar Via Assinada / Comprovante</span>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentSuccessModal({ open: false, payslip: null })}
                  className="w-full text-center py-1.5 text-xs text-gray-500 hover:text-gray-700 font-medium"
                >
                  Concluir e Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeleteConfirm(null)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Excluir Holerite Aprovado
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4">
                Este holerite esta <strong className="text-green-600">APROVADO</strong>. Ao excluir, o valor de <strong className="text-red-600">
                  {Number(deleteConfirm.amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </strong> sera <strong>revertido no caixa</strong> da empresa.
              </p>
              <p className="text-xs text-gray-500 text-center mb-6">
                Funcionario: {deleteConfirm.payslip.full_name}<br />
                Periodo: {monthNames[deleteConfirm.payslip.ref_month - 1]}/{deleteConfirm.payslip.ref_year}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isLoading ? 'Excluindo...' : 'Confirmar Exclusao'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deletePendingConfirm}
        onConfirm={() => {
          if (deletePendingConfirm) {
            deleteMutation.mutate(deletePendingConfirm.payslip.id);
            setDeletePendingConfirm(null);
          }
        }}
        onCancel={() => setDeletePendingConfirm(null)}
        title="Confirmar Exclusao"
        message={`Tem certeza que deseja deletar o holerite de ${deletePendingConfirm?.payslip?.full_name} (${deletePendingConfirm ? monthNames[deletePendingConfirm.payslip.ref_month - 1] : ''}/${deletePendingConfirm?.payslip?.ref_year})? Esta acao nao pode ser desfeita.`}
        confirmText="Deletar"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />

      <ConfirmModal
        show={bulkGenerateConfirm}
        onConfirm={() => {
          setBulkGenerateConfirm(false);
          generateBulkMutation.mutate({
            userIds: users.map(u => u.id),
            year: selectedYear,
            month: selectedMonth,
          });
        }}
        onCancel={() => setBulkGenerateConfirm(false)}
        title="Gerar Previas em Massa"
        message={`Deseja gerar previas para TODOS os ${users.length} funcionarios?`}
        confirmText="Gerar Todos"
        cancelText="Cancelar"
        type="warning"
        isLoading={generateBulkMutation.isLoading}
      />
    </div>
  );
};

const PayslipDetailWrapper = ({ payslipId, onClose, onApprove, onReject }) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const { data: payslipData, refetch } = useQuery({
    queryKey: ['payslip', payslipId],
    queryFn: () => payslipService.getPayslip(payslipId),
    enabled: !!payslipId,
  });

  const payslip = payslipData?.data?.data;

  const updateMutation = useMutation({
    mutationFn: (data) => payslipService.updatePayslip(payslipId, data),
    onSuccess: () => {
      toast.success('Holerite atualizado com sucesso!');
      queryClient.invalidateQueries(['payslip', payslipId]);
      queryClient.invalidateQueries(['payslips-list']);
      refetch();
      setIsEditing(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar holerite.');
    },
  });

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => setIsEditing(false);

  const handleReject = () => {
    const reason = prompt('Motivo da rejeicao:');
    if (reason) onReject(reason);
  };

  if (!payslip) return null;

  return (
    <PayslipDetail
      payslip={payslip}
      onClose={onClose}
      onApprove={onApprove}
      onReject={handleReject}
      onEdit={handleEdit}
      isEditing={isEditing}
      onSave={() => updateMutation.mutate({})}
      onCancel={handleCancel}
    />
  );
};

export default PayslipsManagement;
